import { useState } from 'react';
import clsx from 'clsx';
import {
  ChevronDown,
  ChevronRight,
  Pause,
  Play,
  RefreshCw,
  Save,
  Trash2,
  Settings2,
} from 'lucide-react';
import type { Suite, SuiteSnapshot, RuleState } from '@/lib/checkpoint/types';
import { VerdictList } from './VerdictList';

const OVERALL_TONE: Record<RuleState, { label: string; cls: string }> = {
  pending: { label: '等待中', cls: 'bg-slate-500/30 text-slate-200' },
  pass: { label: '通过', cls: 'bg-emerald-500/30 text-emerald-200' },
  mid: { label: '警告', cls: 'bg-amber-500/30 text-amber-200' },
  fail: { label: '失败', cls: 'bg-rose-500/30 text-rose-200' },
  timeout: { label: '超时', cls: 'bg-rose-500/30 text-rose-200' },
};

interface Props {
  suite: Suite;
  snapshot?: SuiteSnapshot;
  active: boolean;
  onToggleActive: () => void;
  onRemove: () => void;
  onSaveRun?: () => void;
  onEditParams?: () => void;
  onJumpToLine?: (lineId: number) => void;
  /**
   * 重新拉取当前 suite 的最新 YAML、重置 verdict、按当前 logcat 缓冲重放一遍。
   * 用户编辑套件后想"对着已经看到的日志回放校验一遍" 的入口。
   * 返回 Promise，按钮在 pending 期间显示旋转动画。
   */
  onRefresh?: () => Promise<void> | void;
}

export function SuiteCard({
  suite,
  snapshot,
  active,
  onToggleActive,
  onRemove,
  onSaveRun,
  onEditParams,
  onJumpToLine,
  onRefresh,
}: Props) {
  const [open, setOpen] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const handleRefresh = async (): Promise<void> => {
    if (!onRefresh || refreshing) return;
    setRefreshing(true);
    try {
      await onRefresh();
    } finally {
      setRefreshing(false);
    }
  };
  const overall = snapshot?.overallState ?? 'pending';
  const tone = OVERALL_TONE[overall];
  const linesFed = snapshot?.linesFed ?? 0;
  const failCount =
    snapshot?.verdicts.filter((v) => v.state === 'fail' || v.state === 'timeout').length ?? 0;
  const passCount = snapshot?.verdicts.filter((v) => v.state === 'pass').length ?? 0;
  return (
    <section
      className={clsx(
        'rounded-md border bg-bg-card',
        overall === 'fail' ? 'border-rose-500/60' : overall === 'mid' ? 'border-amber-500/50' : 'border-line',
      )}
    >
      <header className="flex items-center gap-1.5 px-2 py-1.5">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="text-ink-muted hover:text-ink"
          title={open ? '折叠' : '展开'}
        >
          {open ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
        </button>
        <h3 className="flex-1 truncate text-xs font-semibold text-ink" title={suite.description}>
          {suite.name}
        </h3>
        <span className={clsx('rounded px-1.5 py-[1px] text-[10px] font-mono', tone.cls)}>
          {tone.label}
        </span>
        <button
          type="button"
          className={clsx(
            'rounded-md border px-1.5 py-0.5 text-[10px]',
            active ? 'border-emerald-500/40 bg-emerald-500/10 text-emerald-300' : 'border-line bg-bg-soft text-ink-muted',
          )}
          title={active ? '暂停 feed' : '恢复 feed'}
          onClick={onToggleActive}
        >
          {active ? <Pause className="h-3 w-3" /> : <Play className="h-3 w-3" />}
        </button>
        {onEditParams && (
          <button
            type="button"
            className="rounded-md border border-line bg-bg-soft px-1.5 py-0.5 text-ink-muted hover:bg-bg-card"
            title="修改参数"
            onClick={onEditParams}
          >
            <Settings2 className="h-3 w-3" />
          </button>
        )}
        {onRefresh && (
          <button
            type="button"
            className="rounded-md border border-line bg-bg-soft px-1.5 py-0.5 text-ink-muted hover:bg-bg-card disabled:opacity-50"
            title="拉取最新 YAML 并按当前日志缓冲重新匹配"
            onClick={() => void handleRefresh()}
            disabled={refreshing}
          >
            <RefreshCw className={clsx('h-3 w-3', refreshing && 'animate-spin')} />
          </button>
        )}
        {onSaveRun && (
          <button
            type="button"
            className="rounded-md border border-line bg-bg-soft px-1.5 py-0.5 text-ink-muted hover:bg-bg-card"
            title="保存当前 run"
            onClick={onSaveRun}
          >
            <Save className="h-3 w-3" />
          </button>
        )}
        <button
          type="button"
          className="rounded-md border border-line bg-bg-soft px-1.5 py-0.5 text-rose-400 hover:bg-bg-card"
          title="移除"
          onClick={onRemove}
        >
          <Trash2 className="h-3 w-3" />
        </button>
      </header>
      <div className="flex items-center gap-2 border-t border-line px-2 py-1 text-[10px] text-ink-muted">
        <span>规则 {suite.rules.length}</span>
        <span className="text-emerald-400">✓ {passCount}</span>
        <span className="text-rose-400">✗ {failCount}</span>
        <span className="ml-auto font-mono">扫描 {linesFed.toLocaleString()} 行</span>
      </div>
      {open && snapshot && (
        <div className="border-t border-line p-1.5">
          <VerdictList verdicts={snapshot.verdicts} onJump={onJumpToLine} />
        </div>
      )}
    </section>
  );
}
